import { FormInstance } from '@modules/forms/components/form-grid/models/form-instance';
import { FormAnswer, isFormAnswerEmpty } from '@pixacare/forms';
import { DraftSadmEntityChangeset } from '../models/draft-sadm-entity-changeset';
import { SadmEntityAnalysisChangeset } from '../models/sadm-entity-analysis-changeset';
import { SadmEntityChangeset } from '../models/sadm-entity-changeset';
import { SadmEntityFormInstances } from '../models/sadm-entity-form-instances';
import { SadmEntityMediaSnapshot } from '../models/sadm-entity-media-snapshot';
import { SadmGridEntity } from '../models/sadm-grid-entity';
import { SavedSadmEntityChangeset } from '../models/saved-sadm-entity-changeset';

const hasChanges = (instance: FormInstance | undefined): instance is FormInstance => {
  if (!instance?.isDirty()) {
    return false;
  }
  const answer = instance.formAnswer();
  return !isFormAnswerEmpty(answer);
};

export const toSadmEntityChangeset = (
  snapshot: SadmEntityMediaSnapshot,
  instances: SadmEntityFormInstances,
): SadmEntityChangeset | null => {
  const key = snapshot.entity.entityKey;
  const protocolAnswer = hasChanges(instances.protocol) ? instances.protocol.formAnswer() : undefined;
  const analysis = buildAnalysis(snapshot, instances.analysis) ?? undefined;

  if (snapshot.entity.sadmEntityId === null) {
    return toDraftChangeset(key, instances.creation, protocolAnswer, analysis);
  }
  return toSavedChangeset(
    key,
    snapshot.entity,
    snapshot.entity.sadmEntityId,
    instances.creation,
    protocolAnswer,
    analysis,
  );
};

const toDraftChangeset = (
  key: string,
  creation: FormInstance | undefined,
  protocolAnswer: FormAnswer | undefined,
  analysis: SadmEntityAnalysisChangeset | undefined,
): DraftSadmEntityChangeset | null => {
  const creationAnswer = creation?.formAnswer();
  const hasContentToSave = hasChanges(creation) || !!protocolAnswer || !!analysis;
  if (!creationAnswer || !hasContentToSave) {
    return null;
  }
  return { key, sadmEntityId: null, creationAnswer, protocolAnswer, analysis };
};

const toSavedChangeset = (
  key: string,
  entity: SadmGridEntity,
  sadmEntityId: number,
  creation: FormInstance | undefined,
  protocolAnswer: FormAnswer | undefined,
  analysis: SadmEntityAnalysisChangeset | undefined,
): SavedSadmEntityChangeset | null => {
  const creationAnswer = hasChanges(creation) ? creation.formAnswer() : undefined;
  if (!creationAnswer && !protocolAnswer && !analysis) {
    return null;
  }
  return {
    key,
    sadmEntityId,
    creationAnswer,
    protocolAnswer,
    analysis,
    currentName: entity.name,
    currentCreationAnswer: entity.creationAnswer,
  };
};

const buildAnalysis = (
  snapshot: SadmEntityMediaSnapshot,
  analysis: FormInstance | undefined,
): SadmEntityAnalysisChangeset | null => {
  const answer = analysis?.formAnswer();
  if (!analysis || !answer) {
    return null;
  }

  const baselineIds = new Set(snapshot.baselineMedias.map((media) => media.id));
  const currentIds = new Set(snapshot.medias.map((media) => media.id));
  // TODO(cross-sequence-medias): drop addedMedias when backend supports attaching existing Media to a different wound
  const addedMedias = snapshot.medias.filter((media) => !baselineIds.has(media.id));
  const removedMediaIds = snapshot.baselineMedias.filter((media) => !currentIds.has(media.id)).map((media) => media.id);

  const hasMediaChanges = snapshot.files.length > 0 || addedMedias.length > 0 || removedMediaIds.length > 0;
  if (!hasChanges(analysis) && !hasMediaChanges) {
    return null;
  }

  return {
    answer,
    sequenceId: snapshot.sequenceId,
    files: snapshot.files,
    removedMediaIds,
    addedMedias,
  };
};
